/** @jsxImportSource theme-ui */
import { jsx } from 'theme-ui';
import PropType from 'prop-types';
import * as hitComps from './hitComps';
import queryString from 'query-string';
import algoliasearch from 'algoliasearch/lite';
import { graphql, useStaticQuery } from 'gatsby';
import { Box, Flex, Heading } from '@theme-ui/components';
import React, { useEffect, useRef, useState } from 'react';
import { InstantSearch, SearchBox } from 'react-instantsearch';
import { Icon } from '../atoms';
import ResultBox from './ResultBox';
import { ActionBanner } from '../molecules';
import { colors, typography } from '../../styles/theme';

const searchClient = algoliasearch(
	process.env.GATSBY_ALGOLIA_APP_ID,
	process.env.GATSBY_ALGOLIA_SEARCH_KEY
);

const urlToSearchState = ({ search }) => queryString.parse(search.slice(1));

const Search = ({ location, indices, regionPath, children }) => {
	const query = urlToSearchState(location);
	const indexName = indices[0].name;
	const searchQuery = { query: query[`${indexName}[query]`] };
	const [searchState, setSearchState] = useState(searchQuery);
	const [isFocused, setIsFocused] = useState(false);
	const containerRef = useRef(null);

	const { resourceImage } = useStaticQuery(graphql`
	{
		resourceImage: file(relativePath: { eq: "resources/resources.png" }) {
			childImageSharp {
				gatsbyImageData(
					placeholder: BLURRED
      		layout: CONSTRAINED
      		width: 283
					height: 283
				)
			}
		}
	}
	`); 

	const handleClickOutside = (event) => {
		if (containerRef.current && !containerRef.current.contains(event.target)) {
			setIsFocused(false);
		}
	};

	useEffect(() => {
		document.addEventListener('click', handleClickOutside, true);
		return () => {
				document.removeEventListener('click', handleClickOutside, true);
		};
	}, []);

	const onSearchStateChange = ({ uiState, setUiState }) => {
		const state = uiState[indices[0].name];
		setSearchState(state);
		setUiState(uiState);
		setIsFocused(false);
		containerRef.current.blur();
	};

	return (
		<InstantSearch
			searchClient={searchClient}
			indexName={indexName}
			onStateChange={onSearchStateChange}
			routing={true}
		>
			<Box>
				<ActionBanner
					theme='primary'
					variant='BodyBannerV4'
					title='Resource Library'
					image={resourceImage}
					customStyles={{
						".banner-v4-image": {
							width: "17.6875rem"
						}
					}}
				>
					<Box
						sx={{
							...styles.inputStyle,
							border: isFocused
								? `2px solid ${colors.primaryMain}`
								: `1px solid ${colors.neutral300}`,
							minHeight: isFocused ? '2.875rem' : '3rem',
						}}
						ref={containerRef}
					>
						<SearchBox
							searchAsYouType={false}
							translations={{ placeholder: 'Search' }}
							placeholder='Search'
							submitIconComponent={({ classNames }) => (
								<Icon
									className={classNames.submitIcon}
									name='Search'
									size={20}
									color={colors.neutral300}
									customStyles={{cursor: "pointer"}}
								/>
							)}
							onFocus={() => setIsFocused(true)}
						/>
					</Box>
				</ActionBanner>
				<Flex sx={styles.container}>
					<Box sx={{ pr: [0, 0, '1.5rem', '1.5rem'], width: '100%' }}>
						<Heading
							as='h2'
							sx={{ ...typography.h2, mb: '1.5rem' }}
						>
							Search: '{searchState?.query}'
						</Heading>
						<ResultBox
							indices={indices}
							regionPath={regionPath}
							hitComponent={hitComps}
						/>
					</Box>
					{children}
				</Flex>
			</Box>
		</InstantSearch>
	);
};

const styles = {
	inputStyle: {
		display: 'flex',
		border: `1px solid ${colors.neutral300}`,
		borderRadius: '0.5rem',
		'&:focus': {
			border: `2px solid ${colors.primaryMain}`,
			transition: 'border-color 0.3s ease-in-out',
		},

		backgroundColor: colors.white,
		width: "100%",
		'.ais-SearchBox': {
			width: '100%',
		},
		'.ais-SearchBox-form': {
			display: 'flex',
			width: '100%',
			...typography.textMd400,
		},
		'.ais-SearchBox-reset': {
			display: 'none !important',
		},
		'.ais-SearchBox-resetIcon': {
			display: 'none !important',
		},
		'.ais-SearchBox-input': {
			outline: 'none',
			border: 'none',
			borderBottomLeftRadius: '0.5rem',
			borderTopLeftRadius: '0.5rem',
			width: '100%',
			p: '0.625rem 0.875rem',
			...typography.textMd400,
			'&::placeholder': {
				...typography.textMd400,
				color: colors.neutral500,
			},
		},
		'.ais-SearchBox-submit': {
			backgroundColor: 'white',
			border: 'none',
			outline: 'none',
			borderBottomRightRadius: '0.5rem',
			borderTopRightRadius: '0.5rem',
			'&:hover': {
				boxShadow: "none",
			},
			p: '0.625rem 0.875rem',
			cursor: "pointer"
		},
		'.ais-SearchBox-submitIcon': {
			width: '1.25rem',
			height: '1.25rem',
			fill: colors.neutral300,
		},
	},
	container: {
		mt: ['3rem', '3rem', '3rem', '7rem'],
		justifyContent: 'space-between',
		mx: "auto",
		maxWidth: "77.5rem",
		flexDirection: ['column-reverse', 'column-reverse', 'row', 'row'],
	},
};

Search.propTypes = {
	location: PropType.object,
	indices: PropType.array,
	regionPath: PropType.string,
	children: PropType.any,
};

export default Search;
